/**
 * Linear Assets — Survey Mission Planner (Step 5)
 *
 * Automated corridor strip-flight waypoint planning along power line and
 * rail centrelines — with full human override of all AI-proposed parameters
 * before the mission is dispatched.
 *
 * Parameter override rationale:
 *   • Altitude  — safety clearances differ between 275kV towers (15 m) and
 *                 electrified rail (6 m); the AI default may need site override.
 *   • Overlap   — stereo 3D reconstruction needs ≥ 85%; quick-look passes use 70%.
 *   • Side-lap  — wide-swathe corridors may reduce from 70% to 60% for efficiency.
 *   • Speed     — slower speeds → sharper imagery; gusty conditions require < 8 m/s.
 *   • Heading   — override to fly parallel vs perpendicular to the centreline.
 *   • GSD       — regulatory evidence capture may require ≤ 2 cm/px override.
 */

import React, { useState } from 'react';
import {
  Navigation, Route, Cpu, ShieldCheck, AlertTriangle,
  RotateCcw, CheckCircle2, ChevronDown, ChevronUp, Edit3,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';

// ─── AI-proposed defaults per asset class ────────────────────────────────────

interface StripFlightParams {
  altitudeAgl: number;        // metres
  forwardOverlap: number;     // percent
  sideLap: number;            // percent
  flightSpeed: number;        // m/s
  headingDeg: number;         // degrees (0 = centreline-parallel)
  gsdCm: number;              // ground sample distance cm/px
  corridorBufferM: number;    // metres each side of centreline
  payload: string;
  secondaryPayload: string;
}

type AssetClass = 'power_line' | 'rail_corridor';

const AI_DEFAULTS: Record<AssetClass, StripFlightParams> = {
  power_line: {
    altitudeAgl:      65,
    forwardOverlap:   85,
    sideLap:          70,
    flightSpeed:      12.5,
    headingDeg:       0,
    gsdCm:            2.5,
    corridorBufferM:  50,
    payload:          'Fixed-Wing RGB + Stereo LiDAR',
    secondaryPayload: 'Thermal (conductor hot-spot)',
  },
  rail_corridor: {
    altitudeAgl:      55,
    forwardOverlap:   80,
    sideLap:          65,
    flightSpeed:      10.0,
    headingDeg:       0,
    gsdCm:            2.0,
    corridorBufferM:  30,
    payload:          'RGB High-Res + LiDAR',
    secondaryPayload: 'Multispectral (vegetation encroachment)',
  },
};

// ─── Target sectors (would be API-driven in production) ───────────────────────

interface Sector {
  id: string;
  label: string;
  detail: string;
  assetClass: AssetClass;
  kmFrom: number;
  kmTo: number;
}

const SECTORS: Sector[] = [
  {
    id: 'pl_7',
    label: 'Power Line Sector 7 (275kV Feeder)',
    detail: 'Encroachment Span: Km 12.4 – Km 14.1 (340 m)',
    assetClass: 'power_line',
    kmFrom: 12.4,
    kmTo: 14.1,
  },
  {
    id: 'pl_11',
    label: 'Power Line Sector 11 (132kV Spur)',
    detail: 'Vegetation Risk Span: Km 5.0 – Km 7.8 (560 m)',
    assetClass: 'power_line',
    kmFrom: 5.0,
    kmTo: 7.8,
  },
  {
    id: 'rail_4b',
    label: 'Rail Corridor 4B (Main-Line North)',
    detail: 'Track Obstruction Zone: Km 88.2 – Km 91.0 (720 m)',
    assetClass: 'rail_corridor',
    kmFrom: 88.2,
    kmTo: 91.0,
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function estimateFlightMin(params: StripFlightParams, sector: Sector): number {
  const distanceM = (sector.kmTo - sector.kmFrom) * 1000;
  const swathWidthM = (params.altitudeAgl * params.gsdCm * 0.01) * 100; // approximation
  const passes = Math.ceil((params.corridorBufferM * 2) / (swathWidthM * (1 - params.sideLap / 100)));
  const totalDistanceM = distanceM * passes;
  return Math.max(5, Math.round(totalDistanceM / params.flightSpeed / 60));
}

function ParamField({
  label, hint, value, min, max, step, unit, onChange, overridden,
}: {
  label: string; hint: string; value: number; min: number; max: number;
  step: number; unit: string; onChange: (v: number) => void; overridden: boolean;
}) {
  return (
    <div className={`p-3 rounded-lg border transition-colors ${overridden ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[11px] font-semibold uppercase tracking-wider ${overridden ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {label} {overridden && <span className="normal-case tracking-normal font-medium">· overridden</span>}
        </span>
        <span className="text-[11px] text-gray-400">{hint}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-24 px-2 py-1.5 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-500 dark:text-gray-400">{unit}</span>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-amber-500"
        />
      </div>
    </div>
  );
}

function PlanRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0 text-sm gap-4">
      <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
      <span className="font-medium text-gray-900 dark:text-white text-right">{value}</span>
    </div>
  );
}

// ─── Page component ───────────────────────────────────────────────────────────

export const LinearSurveyPlannerPage: React.FC = () => {
  const [selectedSectorId, setSelectedSectorId] = useState<string>(SECTORS[0].id);
  const [overrideOpen,     setOverrideOpen]      = useState(false);
  const [params,           setParams]            = useState<StripFlightParams>({ ...AI_DEFAULTS.power_line });
  const [defaults,         setDefaults]          = useState<StripFlightParams>({ ...AI_DEFAULTS.power_line });
  const [pilotNotes,       setPilotNotes]        = useState('');
  const [creating,         setCreating]          = useState(false);
  const [success,          setSuccess]           = useState<string | null>(null);
  const [missionName,      setMissionName]       = useState(SECTORS[0].label);

  const sector = SECTORS.find((s) => s.id === selectedSectorId)!;
  const estimatedMin = estimateFlightMin(params, sector);

  // Count overridden params (numeric and string fields)
  const overriddenFields = (Object.keys(params) as (keyof StripFlightParams)[]).filter(
    (k) => params[k] !== defaults[k]
  );
  const hasOverrides = overriddenFields.length > 0;

  const handleSectorChange = (id: string) => {
    const s = SECTORS.find((x) => x.id === id)!;
    const d = { ...AI_DEFAULTS[s.assetClass] };
    setSelectedSectorId(id);
    setParams(d);
    setDefaults(d);
    setMissionName(s.label);
    setSuccess(null);
  };

  const setParam = <K extends keyof StripFlightParams>(key: K, value: StripFlightParams[K]) => {
    setParams((p) => ({ ...p, [key]: value }));
  };

  const setParamStr = (key: 'payload' | 'secondaryPayload', value: string) => {
    setParams((p) => ({ ...p, [key]: value }));
  };

  const resetToDefaults = () => {
    setParams({ ...defaults });
  };

  const handleDispatch = () => {
    setCreating(true);
    setTimeout(() => {
      setCreating(false);
      setSuccess(
        `Corridor survey mission dispatched — "${missionName}" · ${params.altitudeAgl} m AGL · ` +
        `${params.forwardOverlap}% fwd / ${params.sideLap}% side · ${params.flightSpeed} m/s` +
        (hasOverrides ? ` (${overriddenFields.length} operator override${overriddenFields.length > 1 ? 's' : ''} applied)` : ' (AI defaults used)') +
        '.'
      );
    }, 700);
  };

  const SELECT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Navigation className="w-7 h-7 text-amber-500" /> Linear Survey Mission Planner
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 5 — AI-proposed corridor strip-flight parameters with full operator override before dispatch
          </p>
        </div>
      </div>

      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Left: Sector selector ── */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Target Sector
          </p>
          {SECTORS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => handleSectorChange(s.id)}
              className={[
                'w-full text-left p-4 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500',
                selectedSectorId === s.id
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/10'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-amber-200 dark:hover:border-amber-800',
              ].join(' ')}
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">{s.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.detail}</p>
              <span className={`inline-block mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${s.assetClass === 'power_line' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>
                {s.assetClass === 'power_line' ? 'Power Line' : 'Rail Corridor'}
              </span>
            </button>
          ))}
        </div>

        {/* ── Right: Plan + Override + Dispatch ── */}
        <div className="lg:col-span-2 space-y-5">

          {/* ── AI-Proposed Strip Flight Parameters (read-only summary) ── */}
          <Card>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Cpu className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  AI-Proposed Strip Flight Parameters
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Derived from asset class · corridor geometry · regulatory clearance minima
                </p>
              </div>
              {hasOverrides && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs font-semibold">
                  <Edit3 className="w-3 h-3" /> {overriddenFields.length} override{overriddenFields.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* AI rationale banner */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-800 mb-4">
              <Cpu className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                {sector.assetClass === 'power_line'
                  ? `275kV conductor clearance requires minimum 15 m vertical separation. AI recommends ${defaults.altitudeAgl} m AGL to deliver ${defaults.gsdCm} cm/px GSD for conductor sag and vegetation encroachment detection. Stereo forward overlap set to ${defaults.forwardOverlap}% for point-cloud reconstruction.`
                  : `Electrified rail corridor requires minimum 6 m clearance. AI recommends ${defaults.altitudeAgl} m AGL for ${defaults.gsdCm} cm/px GSD — sufficient for gauge deviation and ballast obstruction classification. Slower survey speed (${defaults.flightSpeed} m/s) reduces motion blur on track geometry.`
                }
              </p>
            </div>

            {/* Parameter summary table */}
            <PlanRow label="Primary Payload"    value={<span className={params.payload !== defaults.payload ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{params.payload}{params.payload !== defaults.payload ? ' ✱' : ''}</span>} />
            <PlanRow label="Secondary Payload"  value={<span className={params.secondaryPayload !== defaults.secondaryPayload ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>{params.secondaryPayload}{params.secondaryPayload !== defaults.secondaryPayload ? ' ✱' : ''}</span>} />
            <PlanRow label="Corridor Buffer"    value={`± ${params.corridorBufferM} m each side of centreline`} />
            <PlanRow label="Estimated Flight Time" value={`~${estimatedMin} min`} />
            <PlanRow
              label="Waypoint Count (est.)"
              value={`~${Math.max(4, Math.round(estimatedMin * params.flightSpeed / 100))} waypoints along centreline`}
            />

            {/* Override accordion trigger */}
            <button
              type="button"
              onClick={() => setOverrideOpen((v) => !v)}
              className="mt-5 w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/10 hover:bg-amber-50 dark:hover:bg-amber-950/20 transition-colors text-sm font-semibold text-amber-700 dark:text-amber-400"
            >
              <span className="flex items-center gap-2">
                <Edit3 className="w-4 h-4" />
                {overrideOpen ? 'Hide' : 'Override'} Strip Flight Parameters
                {hasOverrides && !overrideOpen && (
                  <span className="font-normal text-amber-600 dark:text-amber-500">
                    ({overriddenFields.length} field{overriddenFields.length > 1 ? 's' : ''} modified)
                  </span>
                )}
              </span>
              {overrideOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {/* ── Override panel ── */}
            {overrideOpen && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Adjust any parameter below. Changed fields are highlighted amber. AI defaults are restored on sector change.
                  </p>
                  {hasOverrides && (
                    <button
                      type="button"
                      onClick={resetToDefaults}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      <RotateCcw className="w-3 h-3" /> Reset to AI defaults
                    </button>
                  )}
                </div>

                {/* Payload text fields */}
                <div className="space-y-3 mb-3">
                  {(['payload', 'secondaryPayload'] as const).map((k) => {
                    const changed = params[k] !== defaults[k];
                    const label = k === 'payload' ? 'Primary Payload' : 'Secondary Payload';
                    return (
                      <div key={k} className={`p-3 rounded-lg border transition-colors ${changed ? 'border-amber-300 dark:border-amber-600 bg-amber-50/40 dark:bg-amber-950/10' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-[11px] font-semibold uppercase tracking-wider ${changed ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>{label}{changed ? ' · overridden' : ''}</span>
                          <span className="text-[11px] text-gray-400">AI: {defaults[k]}</span>
                        </div>
                        <input
                          type="text"
                          value={params[k]}
                          onChange={(e) => setParamStr(k, e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ParamField
                    label="Corridor Altitude"
                    hint={`AI default: ${defaults.altitudeAgl} m`}
                    value={params.altitudeAgl}
                    min={25} max={150} step={5} unit="m AGL"
                    onChange={(v) => setParam('altitudeAgl', v)}
                    overridden={params.altitudeAgl !== defaults.altitudeAgl}
                  />
                  <ParamField
                    label="Forward Overlap"
                    hint={`AI default: ${defaults.forwardOverlap}%`}
                    value={params.forwardOverlap}
                    min={60} max={95} step={5} unit="%"
                    onChange={(v) => setParam('forwardOverlap', v)}
                    overridden={params.forwardOverlap !== defaults.forwardOverlap}
                  />
                  <ParamField
                    label="Side-lap"
                    hint={`AI default: ${defaults.sideLap}%`}
                    value={params.sideLap}
                    min={50} max={90} step={5} unit="%"
                    onChange={(v) => setParam('sideLap', v)}
                    overridden={params.sideLap !== defaults.sideLap}
                  />
                  <ParamField
                    label="Flight Speed"
                    hint={`AI default: ${defaults.flightSpeed} m/s`}
                    value={params.flightSpeed}
                    min={4} max={20} step={0.5} unit="m/s"
                    onChange={(v) => setParam('flightSpeed', v)}
                    overridden={params.flightSpeed !== defaults.flightSpeed}
                  />
                  <ParamField
                    label="Corridor Buffer"
                    hint={`AI default: ±${defaults.corridorBufferM} m`}
                    value={params.corridorBufferM}
                    min={10} max={150} step={5} unit="m"
                    onChange={(v) => setParam('corridorBufferM', v)}
                    overridden={params.corridorBufferM !== defaults.corridorBufferM}
                  />
                  <ParamField
                    label="Target GSD"
                    hint={`AI default: ${defaults.gsdCm} cm/px`}
                    value={params.gsdCm}
                    min={1} max={10} step={0.5} unit="cm/px"
                    onChange={(v) => setParam('gsdCm', v)}
                    overridden={params.gsdCm !== defaults.gsdCm}
                  />
                  <ParamField
                    label="Survey Heading"
                    hint="0° = centreline-parallel"
                    value={params.headingDeg}
                    min={0} max={359} step={5} unit="°"
                    onChange={(v) => setParam('headingDeg', v)}
                    overridden={params.headingDeg !== defaults.headingDeg}
                  />
                </div>

                {/* Safety guard: altitude vs asset class */}
                {sector.assetClass === 'power_line' && params.altitudeAgl < 40 && (
                  <Alert type="error">
                    <strong>Safety constraint:</strong> Altitude below 40 m AGL on a 275kV corridor is below CAAB/DGCA minimum separation. Raise to at least 40 m or request an airspace exemption.
                  </Alert>
                )}
                {sector.assetClass === 'rail_corridor' && params.altitudeAgl < 25 && (
                  <Alert type="error">
                    <strong>Safety constraint:</strong> Altitude below 25 m AGL on an electrified rail corridor does not provide sufficient OHE clearance. Raise to at least 25 m.
                  </Alert>
                )}
                {params.flightSpeed > 16 && (
                  <Alert type="warning">
                    Flight speed above 16 m/s may introduce significant motion blur at the target GSD. Verify sensor shutter speed supports this velocity.
                  </Alert>
                )}
              </div>
            )}
          </Card>

          {/* ── Safety & Regulatory Checklist ── */}
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Safety &amp; Regulatory Pre-flight Checklist
              </h3>
            </div>
            <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
              {[
                sector.assetClass === 'power_line'
                  ? 'Notify transmission operator (e.g. PGCIL / National Grid) ≥ 24 h prior — EMF and induced current hazard'
                  : 'Notify railway operations control ≥ 4 h prior — trackside personnel clearance required',
                'File DGCA / CAAB airspace notification for corridor survey area',
                'Verify approved drone has RF-shielded electronics for HV line proximity',
                'Confirm battery charge plan covers estimated flight + 15 % reserve',
                'Wind < 10 m/s; no precipitation; visibility > 5 km',
                'Log pilot licence number and UAS serial in operations log before takeoff',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
              {hasOverrides && (
                <li className="flex items-start gap-2 pt-1 border-t border-gray-100 dark:border-gray-800 mt-1">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-amber-700 dark:text-amber-400">Operator overrides applied</strong> — record justification in operations log as required by QA protocol.
                  </span>
                </li>
              )}
            </ul>
          </Card>

          {/* ── Mission Creation ── */}
          <Card>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Route className="w-5 h-5 text-amber-500" /> Create &amp; Dispatch Mission
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mission Name
                </label>
                <input
                  className={SELECT_CLS}
                  value={missionName}
                  onChange={(e) => setMissionName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Pilot / Operator Notes
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Coordination with tower crew on-site at Km 13.0 until 11:00; avoid south approach."
                  className={`${SELECT_CLS} resize-none`}
                  value={pilotNotes}
                  onChange={(e) => setPilotNotes(e.target.value)}
                />
              </div>

              {/* Final parameter summary before dispatch */}
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs space-y-1">
                <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Final Mission Parameters</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: 'Altitude', value: `${params.altitudeAgl} m AGL`, changed: params.altitudeAgl !== defaults.altitudeAgl },
                    { label: 'Fwd Overlap', value: `${params.forwardOverlap}%`, changed: params.forwardOverlap !== defaults.forwardOverlap },
                    { label: 'Side-lap', value: `${params.sideLap}%`, changed: params.sideLap !== defaults.sideLap },
                    { label: 'Speed', value: `${params.flightSpeed} m/s`, changed: params.flightSpeed !== defaults.flightSpeed },
                    { label: 'Buffer', value: `±${params.corridorBufferM} m`, changed: params.corridorBufferM !== defaults.corridorBufferM },
                    { label: 'GSD', value: `${params.gsdCm} cm/px`, changed: params.gsdCm !== defaults.gsdCm },
                    { label: 'Heading', value: `${params.headingDeg}°`, changed: params.headingDeg !== defaults.headingDeg },
                    { label: 'Est. Time', value: `~${estimatedMin} min`, changed: false },
                  ].map(({ label, value, changed }) => (
                    <div
                      key={label}
                      className={`p-2 rounded border ${changed ? 'border-amber-300 bg-amber-50/60 dark:bg-amber-950/10 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'}`}
                    >
                      <span className={`block text-[10px] uppercase tracking-wider mb-0.5 ${changed ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-gray-400'}`}>
                        {label}{changed ? ' ✱' : ''}
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                variant="primary"
                onClick={handleDispatch}
                disabled={creating}
                className="w-full"
              >
                {creating ? 'Dispatching…' : `Dispatch Corridor Survey Mission${hasOverrides ? ' (with overrides)' : ''}`}
              </Button>
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default LinearSurveyPlannerPage;
